import type { CreateInterviewResponse } from "@shared/schema";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { InterviewSchedulingSection } from "@/components/interview-scheduling-section";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useInterviewerAssignment } from "@/hooks/use-interviewer-assignment";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";

interface ScheduleInterviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applicationId: string;
  stageId: string;
  stageName: string;
  candidateName: string;
  jobOpeningId: string;
  durationMinutes?: number;
  stageDescription?: string;
  jobTitle?: string;
  /** Current pipeline status of the candidate — used to detect whether creating
   * this interview also implies moving them into this stage. */
  applicationStatus?: string;
  /** Interview stage the candidate is currently in (null when not interviewing). */
  currentInterviewStageId?: string | null;
}

export function ScheduleInterviewDialog({
  open,
  onOpenChange,
  applicationId,
  stageId,
  stageName,
  candidateName,
  jobOpeningId,
  durationMinutes = 60,
  stageDescription = "",
  jobTitle = "",
  applicationStatus,
  currentInterviewStageId = null,
}: ScheduleInterviewDialogProps) {
  const { toast } = useToast();

  const assignment = useInterviewerAssignment({
    jobOpeningId,
    stageId,
    enabled: open,
  });

  // Email state
  const [sendEmail, setSendEmail] = useState(true);
  const [emailStageName, setEmailStageName] = useState(stageName);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailIntroText, setEmailIntroText] = useState("");
  const [emailExtraBody, setEmailExtraBody] = useState("");
  const [emailButtonText, setEmailButtonText] = useState("Agendar Entrevista");
  const [emailClosingText, setEmailClosingText] = useState(
    "Se tiver dúvidas, não hesite em nos contatar.\nBoa sorte!",
  );

  // Idempotency confirm
  const [confirmOpen, setConfirmOpen] = useState(false);
  // "Move candidate to this stage?" confirm
  const [moveConfirmOpen, setMoveConfirmOpen] = useState(false);
  // Remembers the move decision so the idempotency "create anyway" path keeps it.
  const [pendingMoveToStage, setPendingMoveToStage] = useState(false);

  // The candidate is not yet in this interview stage — creating the interview
  // here would otherwise leave them in their current column.
  const needsMove = applicationStatus !== "interviewing" || currentInterviewStageId !== stageId;

  // Reset email defaults when opening
  useEffect(() => {
    if (!open) return;
    const jTitle = jobTitle || "a vaga";
    setEmailStageName(stageName);
    setEmailSubject(`Próxima etapa: ${stageName} — ${jTitle}`);
    setEmailIntroText(
      `Temos uma novidade! Você avançou no processo seletivo para ${jTitle} e queremos te convidar para a próxima etapa.`,
    );
    setEmailButtonText("Agendar Entrevista");
    setEmailClosingText("Qualquer dúvida, é só responder este email.\nBoa sorte! 🍀");
    setEmailExtraBody("");
    setSendEmail(true);
  }, [open, stageName, jobTitle]);

  const createMutation = useMutation({
    mutationFn: async ({ force, moveToStage }: { force?: boolean; moveToStage?: boolean } = {}) => {
      const payload: Record<string, unknown> = {
        applicationId,
        interviewStageId: stageId,
        sendEmail,
        force: force || false,
        moveToStage: moveToStage || false,
        emailStageNameOverride: emailStageName || undefined,
        emailSubjectOverride: emailSubject || undefined,
        emailIntroOverride: emailIntroText || undefined,
        emailExtraBody: emailExtraBody || undefined,
        emailButtonTextOverride: emailButtonText || undefined,
        emailClosingOverride: emailClosingText || undefined,
      };
      if (assignment.selectedGroupId) {
        payload.interviewerGroupId = assignment.selectedGroupId;
      } else {
        payload.interviewerId = assignment.interviewerId;
      }
      return rpcClient.interviews.create(
        payload as Parameters<typeof rpcClient.interviews.create>[0],
      );
    },
    onSuccess: (data: CreateInterviewResponse) => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId } })
          .queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: orpc.interviews.getByApplication.queryOptions({ input: { applicationId } })
          .queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getJobKanban.queryOptions({ input: { jobOpeningId } }).queryKey,
      });
      if (data.emailFailed) {
        toast({
          title: "Entrevista criada",
          description:
            "A entrevista foi criada, mas o envio do email falhou. Reenvie pelo card de detalhes do candidato.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Entrevista criada",
          description: "Nova entrevista agendada para esta etapa.",
        });
      }
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      if (err instanceof Error && err.message.includes("recentlyCreated")) {
        setConfirmOpen(true);
        return;
      }
      // Check for CONFLICT errors from oRPC
      const errorObj = err as { code?: string; data?: { recentlyCreated?: boolean } };
      if (errorObj.code === "CONFLICT" && errorObj.data?.recentlyCreated) {
        setConfirmOpen(true);
        return;
      }
      toast({
        title: "Erro",
        description: err instanceof Error ? err.message : "Erro ao criar entrevista",
        variant: "destructive",
      });
    },
  });

  return (
    <>
      <Dialog open={open && !confirmOpen && !moveConfirmOpen} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agendar Entrevista — {stageName}</DialogTitle>
          </DialogHeader>

          <InterviewSchedulingSection
            assignmentOptions={assignment.assignmentOptions}
            people={assignment.people}
            isLoading={assignment.isLoading}
            interviewerId={assignment.interviewerId}
            setInterviewerId={assignment.setInterviewerId}
            selectedGroupId={assignment.selectedGroupId}
            setSelectedGroupId={assignment.setSelectedGroupId}
            assignmentMode={assignment.assignmentMode}
            setAssignmentMode={assignment.setAssignmentMode}
            sendEmailToCandidate={sendEmail}
            setSendEmailToCandidate={setSendEmail}
            emailStageName={emailStageName}
            setEmailStageName={setEmailStageName}
            emailSubject={emailSubject}
            setEmailSubject={setEmailSubject}
            emailIntroText={emailIntroText}
            setEmailIntroText={setEmailIntroText}
            emailExtraBody={emailExtraBody}
            setEmailExtraBody={setEmailExtraBody}
            emailButtonText={emailButtonText}
            setEmailButtonText={setEmailButtonText}
            emailClosingText={emailClosingText}
            setEmailClosingText={setEmailClosingText}
            candidateName={candidateName}
            durationMinutes={durationMinutes}
            stageDescription={stageDescription}
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              disabled={!assignment.hasSelection || createMutation.isPending}
              onClick={() => {
                if (needsMove) {
                  setMoveConfirmOpen(true);
                  return;
                }
                createMutation.mutate({});
              }}
            >
              {createMutation.isPending ? "Criando..." : "Agendar Entrevista"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Entrevista recente detectada</AlertDialogTitle>
            <AlertDialogDescription>
              Uma entrevista foi criada nesta etapa há menos de 5 minutos. Tem certeza que deseja
              criar outra?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setConfirmOpen(false);
              }}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false);
                createMutation.mutate({ force: true, moveToStage: pendingMoveToStage });
              }}
            >
              Sim, criar mesmo assim
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={moveConfirmOpen} onOpenChange={setMoveConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mover candidato para esta etapa?</AlertDialogTitle>
            <AlertDialogDescription>
              {candidateName} ainda não está na etapa <strong>{stageName}</strong>. Deseja mover o
              candidato para esta etapa ao agendar a entrevista, ou apenas criar a entrevista sem
              alterar a etapa atual?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setMoveConfirmOpen(false)}>
              Cancelar
            </AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => {
                setMoveConfirmOpen(false);
                setPendingMoveToStage(false);
                createMutation.mutate({ moveToStage: false });
              }}
            >
              Só agendar
            </Button>
            <AlertDialogAction
              onClick={() => {
                setMoveConfirmOpen(false);
                setPendingMoveToStage(true);
                createMutation.mutate({ moveToStage: true });
              }}
            >
              Mover e agendar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
